import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, BarChart3, GraduationCap, User } from "lucide-react";
import { useAuth } from "../context/AuthContext";

/**
 * Mobile-only persistent bottom navigation (4 tabs).
 * Hidden on md+ screens where the top Navbar handles navigation.
 *
 * Tab → route mapping per product brief:
 *   Dashboard/Home   → /dashboard (or / when logged-out)
 *   Markets/Plots    → /watchlist  (the user's tracked markets)
 *   Learning Hub     → /learn
 *   Profile/Settings → /settings   (or /login when logged-out)
 */
const TABS_AUTHED = [
  { key: "dashboard", to: "/dashboard", label: "Home", icon: Home, match: (p) => p === "/dashboard" || p === "/" },
  { key: "markets", to: "/watchlist", label: "Markets", icon: BarChart3, match: (p) => p.startsWith("/watchlist") || p.startsWith("/asset") || p.startsWith("/screener") },
  { key: "learn", to: "/learn", label: "Learn", icon: GraduationCap, match: (p) => p.startsWith("/learn") || p.startsWith("/sandbox") },
  { key: "profile", to: "/settings", label: "Profile", icon: User, match: (p) => p.startsWith("/settings") || p.startsWith("/paper") || p.startsWith("/alerts") || p.startsWith("/wheel") },
];

const TABS_GUEST = [
  { key: "dashboard", to: "/", label: "Home", icon: Home, match: (p) => p === "/" },
  { key: "markets", to: "/screener", label: "Markets", icon: BarChart3, match: (p) => p.startsWith("/screener") || p.startsWith("/asset") },
  { key: "learn", to: "/learn", label: "Learn", icon: GraduationCap, match: (p) => p.startsWith("/learn") || p.startsWith("/sandbox") },
  { key: "profile", to: "/login", label: "Sign in", icon: User, match: (p) => p.startsWith("/login") || p.startsWith("/register") },
];

export default function BottomNav() {
  const { user } = useAuth();
  const location = useLocation();
  const tabs = user ? TABS_AUTHED : TABS_GUEST;

  // Apply body padding so content isn't hidden behind the fixed bar on mobile.
  // Removed when the component unmounts (e.g. some debug routes).
  useEffect(() => {
    document.body.classList.add("has-bottom-nav");
    return () => document.body.classList.remove("has-bottom-nav");
  }, []);

  return (
    <nav
      data-testid="bottom-nav"
      className="md:hidden fixed bottom-0 inset-x-0 z-[10000] bg-white/95 dark:bg-[#1F2421]/95 backdrop-blur-md border-t border-[#EAE6E1] dark:border-[#2C302E]"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0)" }}
      aria-label="Primary navigation"
    >
      <ul className="grid grid-cols-4 gap-1 px-1 py-1.5">
        {tabs.map((t) => {
          const active = t.match(location.pathname);
          const Icon = t.icon;
          return (
            <li key={t.key}>
              <Link
                to={t.to}
                data-testid={`bottom-nav-${t.key}`}
                aria-current={active ? "page" : undefined}
                className={`flex flex-col items-center justify-center gap-0.5 py-1.5 rounded-xl transition-soft no-touch-min ${
                  active
                    ? "text-[#2A2F2B] dark:text-[#E8E6E2] bg-[#EAE6E1]/60 dark:bg-[#2C302E]/60"
                    : "text-[#6B7068] dark:text-[#9CA0A0] hover:text-[#2A2F2B] dark:hover:text-[#E8E6E2]"
                }`}
              >
                <Icon className="w-5 h-5" strokeWidth={active ? 2 : 1.5} />
                <span className="text-[10px] font-medium leading-none">{t.label}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
